import React, { FC } from 'react';

import {
  getLabelsSelected,
  getLabelsVisible,
} from '../../../../containers/compose/helpers';

import { LabelBasicFragment } from '../../../../generated/graphql';
import translate from '../../../../i18n/translate';

import { getCanCreateLabel } from '../../../../utils/permissions/label';
import MetaLabelCreate from '../../../MetaLabels/MetaLabelCreate';
import useLabelsMode from '../../../MetaLabels/useLabelsMode';
import TabSearch from '../TabCheckboxSearch';
import SearchNoResult from '../TabCheckboxSearch/SearchNoResult';

import * as Styled from './styled';

type Props = {
  dataAll: LabelBasicFragment[];
  dataSearchValue: string;
  dataSelected: Set<LabelBasicFragment['id']>;
  isLoading: boolean;
  requestCreate: (
    title: LabelBasicFragment['text'],
    color: LabelBasicFragment['color'],
  ) => void;
  requestSearch: (term: string) => void;
  requestToggle: (labelId: LabelBasicFragment['id'], state: boolean) => void;
};

/**
 * Labels panel,
 * Consists of a filter box,
 * and labels list (with a checkbox for each of them)
 *
 * @param props                 Props passed to the component
 * @param props.dataAll         All available labels
 * @param props.dataSearchValue <input type="search" /> value attribute
 * @param props.dataSelected    Which labels are already selected for the message
 * @param props.isLoading       Whether the data is being fetched
 * @param props.requestCreate   Request that a new label is created (will be null if the user has no permission to create labels)
 * @param props.requestSearch   Callback to invoke when search term is changed
 * @param props.requestToggle   Callback to be invoked when a label is a/detached
 * @returns                     The component itself
 */
const Labels: FC<Props> = ({
  dataAll,
  dataSearchValue,
  dataSelected,
  isLoading,
  requestCreate,
  requestSearch,
  requestToggle,
}) => {
  const [, mode, { setModeView }] = useLabelsMode('compose');

  const labelsExisting = dataAll.map(label => label.text);
  const selectedLabels = getLabelsSelected(dataAll, dataSelected);

  const dataLabelsVisible = getLabelsVisible(dataAll, {
    labelsSearch: dataSearchValue,
  });

  const isEmpty = dataAll.length === 0;
  const isSearching = dataSearchValue.trim().length !== 0;

  const shouldShowNoResults = isEmpty && !isLoading && !isSearching;

  const canCreateLabel = getCanCreateLabel();
  const createLabelButton = (
    <MetaLabelCreate
      isActive={mode === 'MODE_CREATE'}
      labelsExisting={labelsExisting}
      requestCreate={requestCreate}
      requestModeView={setModeView}
    />
  );

  if (shouldShowNoResults) {
    return (
      <SearchNoResult
        fallback={
          canCreateLabel ? (
            <Styled.LabelsCreateWrapper data-is-fallback={true}>
              {createLabelButton}
            </Styled.LabelsCreateWrapper>
          ) : null
        }
      />
    );
  }

  return (
    <Styled.SectionWrapper>
      <Styled.LabelsCreateWrapper>
        {createLabelButton}
      </Styled.LabelsCreateWrapper>
      <TabSearch
        dataSearchValue={dataSearchValue}
        onSearch={requestSearch}
        placeholder={translate('SEARCH__LABELS')}
        sections={[
          {
            dataSelected,
            isLoading,
            isSearching,
            itemsFiltered: dataLabelsVisible,
            itemsSelected: selectedLabels,
            onChange: requestToggle,
            type: 'labels',
          },
        ]}
      />
    </Styled.SectionWrapper>
  );
};

export default Labels;
